import { QueryStatus } from '@reduxjs/toolkit/query';
import '@shoelace-style/shoelace/dist/components/breadcrumb-item/breadcrumb-item';
import '@shoelace-style/shoelace/dist/components/breadcrumb/breadcrumb';
import '@shoelace-style/shoelace/dist/components/button/button';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item';
import '@shoelace-style/shoelace/dist/components/menu/menu';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { CategoryStats, ProductSearchQueryArgs, ProductsQuery, ProductStatsQuery } from '../api/models';
import { NavigateHomeEvent, SearchEvent } from '../events';
import { Layout } from '../state/pages';
import { styles as sharedStyles } from '../styles/shared-styles';

@customElement('search-navigation')
export class SearchNavigation extends LitElement {

  static get styles() {
    return [
      sharedStyles,
      css`
      badge-container {
        display: flex; /* Use flexbox to create a horizontal layout */
        flex-wrap: nowrap; /* Prevent badges from wrapping onto new lines */
        overflow-x: auto; /* Allow horizontal scrolling if badges exceed the container width */
        padding: 10px; /* Add padding for spacing */        
      }
    `]
  };

  @property({ attribute: true, type: Number })
  layout: Layout = Layout.PHONE;

  @property({ attribute: true, type: Object })
  productStatsQuery: ProductStatsQuery | null = null;

  @property({ attribute: true, type: Object })
  productsSearchQuery: ProductsQuery | null = null;

  renderCategorySuggestions() {
    const productSearchQueryArgs: ProductSearchQueryArgs = this.productsSearchQuery?.originalArgs as ProductSearchQueryArgs;
    if (this.productsSearchQuery?.status === QueryStatus.fulfilled &&
      (productSearchQueryArgs.category_ids || []).length > 0 &&
      (productSearchQueryArgs.category_ids || []).length < Object.keys(this.productsSearchQuery.data?.stats?.categoryStats || {}).length &&
      this.productsSearchQuery?.data?.results.length) {
      // show sub categories if searching within a category
      return html`
    <sl-breadcrumb-item>
    <sl-dropdown slot="suffix">
      <sl-button slot="trigger" size="small" circle>
        <sl-icon label="Categories" name="three-dots"></sl-icon>
      </sl-button>
      <sl-menu>
      ${Object.values(this.productsSearchQuery.data.stats?.categoryStats || {})
          .filter(stat => (productSearchQueryArgs?.category_ids || []).indexOf(stat.category) < 0)
          .sort((a, b) => (b.product_count || 0) - (a.product_count || 0))
          .slice(0, 8)
          .map(categoryStat => html`
                  <sl-menu-item @click=${(e: TouchEvent) => this.selectCategory(e, categoryStat.category || "")}>
                    ${categoryStat.category} (${this.productStatsQuery?.data?.categoryMap ? this.productStatsQuery?.data?.categoryMap[categoryStat.category].product_count : categoryStat.product_count})
                  </sl-menu-item>
                `)}
      </sl-menu>
    </sl-dropdown>
    </sl-breadcrumb-item>`;
    } else if ((productSearchQueryArgs.category_ids || []).length == 0
      && this.productStatsQuery?.data?.categoryMap) {
      // show top categories
      return html`
        <sl-breadcrumb-item>
        <sl-dropdown slot="suffix">
          <sl-button slot="trigger" size="small" circle>
            <sl-icon label="Categories" name="three-dots"></sl-icon>
          </sl-button>
            <sl-menu>
              <sl-menu-label>Categories</sl-menu-label>
        ${Object.values(this.productStatsQuery?.data?.categoryMap || {})
          .filter(categoryStat => categoryStat.level === 1)
          .sort((a, b) => a.category.localeCompare(b.category))
          .map(categoryStat => html`
                  <sl-menu-item @click=${(event:TouchEvent) => this.selectCategory(event, categoryStat.category || "")}>                    
                    ${categoryStat.category} (${categoryStat.product_count})
                  </sl-menu-item>
                `)}
              <sl-divider></sl-divider>        
              </sl-menu>      `;
    } else {
      return nothing;
    }

  }


  render() {
    if (this.productStatsQuery?.status !== QueryStatus.fulfilled) return nothing;
    if (this.productsSearchQuery?.status !== QueryStatus.fulfilled) return nothing;
    const productSearchQueryArgs: ProductSearchQueryArgs = this.productsSearchQuery?.originalArgs as ProductSearchQueryArgs;
    return html`
        <sl-breadcrumb>
          <sl-breadcrumb-item @click=${this.clickHome}>
            <sl-icon slot="prefix" name="house"></sl-icon>
            start
          </sl-breadcrumb-item>
          ${productSearchQueryArgs.shop_ids?.map((value: string) =>
      html`
            <sl-breadcrumb-item>
              ${value}
              <sl-icon slot="prefix" name="cart3"></sl-icon>
            </sl-breadcrumb-item>`
    )}
    ${repeat((productSearchQueryArgs.category_ids || [])
      .map(category_id => this.productStatsQuery!.data!.categoryMap![category_id])
      .filter(stat => !!stat)
      .sort((a, b) => ((a?.level || 1) - (b?.level || 1))),
      (categoryStat, _index) => html`
            <sl-breadcrumb-item @click=${(event: TouchEvent) => this.gotoCategory(event, categoryStat!)}>
                <sl-icon slot="prefix" name="tag"></sl-icon>
                ${categoryStat!.category}
            </sl-breadcrumb-item>`
    )}
    ${this.renderCategorySuggestions()}
    ${!!productSearchQueryArgs.q ?
        html`
            <sl-breadcrumb-item>
              Zoeken naar <strong>${productSearchQueryArgs.q}</strong>
              <sl-icon slot="prefix" name="search"></sl-icon>
            </sl-breadcrumb-item>`
        : nothing
      }
    </sl-breadcrumb>`;
  }

  //   <sl-dropdown>
  //   <sl-menu>
  //     ${this.productStatsQuery?.data?.categoryStats
  //       .filter(stat => stat.level === categoryStat!.level)
  //       .map(stat => html`
  //         <sl-menu-item @click=${(event: TouchEvent) => this.switchOtherCategory(event, stat)}>
  //           ${stat.category} (${stat.product_count})
  //         </sl-menu-item>
  //       `)}
  //   </sl-menu>
  // </sl-dropdown>


  private clickHome(touchEvent: TouchEvent) {
    touchEvent.preventDefault();
    this.dispatchEvent(new NavigateHomeEvent());
  }

  private gotoCategory(touchEvent: TouchEvent, categoryStat: CategoryStats) {
    touchEvent.stopPropagation()
    touchEvent.preventDefault();
    const productSearchQueryArgs: ProductSearchQueryArgs = this.productsSearchQuery?.originalArgs as ProductSearchQueryArgs;
    // goto the selected category but include all the parent categories !!!
    const category_and_parents_ids: string[] = (productSearchQueryArgs.category_ids || [])
      .map(category_id => this.productStatsQuery?.data?.categoryMap?.[category_id] || null)
      .filter(stat => !!stat)
      .filter(stat => stat!.level < categoryStat.level || stat!.category === categoryStat.category)
      .map(stat => stat!.category);
    const newSearchQueryArgs: ProductSearchQueryArgs = Object.assign({}, productSearchQueryArgs, { category_ids: category_and_parents_ids }, { q: "" })
    this.dispatchEvent(new SearchEvent(newSearchQueryArgs));
  }

  selectCategory(touchEvent: TouchEvent, category: string) {
    touchEvent.preventDefault();
    touchEvent.stopPropagation();
    const productSearchQueryArgs: ProductSearchQueryArgs = this.productsSearchQuery?.originalArgs as ProductSearchQueryArgs;
    const cats = [...(productSearchQueryArgs?.category_ids || []), category];
    const args = Object.assign({}, productSearchQueryArgs, { category_ids: [...new Set(cats)] }, { q: "" });
    this.dispatchEvent(new SearchEvent(args));
  }
  // private switchOtherCategory(touchEvent: TouchEvent, categoryStat: CategoryStats) {
  //   touchEvent.preventDefault();
  //   touchEvent.stopPropagation()
  //   const category_ids: string[] = this.categoryStats.filter(stat=>stat.level<categoryStat.level).map(stat=>stat.category);
  //   category_ids.push(categoryStat.category)
  //   const newSearchQueryArgs: ProductSearchQueryArgs = Object.assign({}, this.productSearchQueryArgs, { category_ids })
  //   this.dispatchEvent(new SearchEvent(newSearchQueryArgs));
  // }
}

